import React, { useState, useEffect } from 'react';
import { 
  X, 
  Search, 
  Plus, 
  Trash2, 
  Save, 
  Loader2, 
  ChevronRight,
  Package,
  Calendar,
  User,
  CreditCard,
  Hash,
  ArrowRight,
  PlusCircle,
  Building2,
  Mail,
  Phone,
  MapPin,
  HelpCircle,
  FileSearch,
  CheckCircle2,
  AlertCircle,
  Info,
  Clock,
  Receipt,
  Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ManagedDocument, 
  DocumentType, 
  PaymentCondition, 
  ManagedArticle, 
  DocumentItem,
  Account,
  ManagedContact,
  ContactIdType,
  Transaction,
  TransactionType,
  TransactionDetail,
  Currency
} from '../../types';
import { calculateTransactionBalances } from '../../utils/transactionBalance';
import { createManagedDocument, updateManagedDocument, subscribeToContacts, createContact, subscribeToDocuments, generateUniqueLocatorCode, deleteManagedDocument } from '../../services/managementService';
import { subscribeToTransactions } from '../../services/financeService';
import { getInstitutionById } from '../../constants/institutions';
import { useClosingDate } from '../../hooks/useClosingDate';

interface DocumentFormProps {
  userId: string;
  initialType: DocumentType;
  editingDocument?: ManagedDocument | null;
  articles: ManagedArticle[];
  accounts: Account[];
  onClose: () => void;
  onSuccess: () => void;
}

export const DocumentForm: React.FC<DocumentFormProps> = ({ 
  userId, 
  initialType, 
  editingDocument, 
  articles, 
  accounts,
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<DocumentItem[]>([]);
  
  // Header state
  const [type, setType] = useState<DocumentType>(initialType);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [number, setNumber] = useState('');
  const [locatorCode, setLocatorCode] = useState('');
  const [timbrado, setTimbrado] = useState('');
  const [cdc, setCdc] = useState('');
  const [contactId, setContactId] = useState('');
  const [contactName, setContactName] = useState('');
  const [internalContactId, setInternalContactId] = useState('');
  const [internalContactName, setInternalContactName] = useState('');
  const [condition, setCondition] = useState<PaymentCondition>('CONTADO');
  const [observations, setObservations] = useState('');
  const [tax5Amount, setTax5Amount] = useState<string>('');
  const [tax10Amount, setTax10Amount] = useState<string>('');
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);
  const [isHistorical, setIsHistorical] = useState(false);
  const [historicalPendingAmount, setHistoricalPendingAmount] = useState<string>('');
  const [parentDocumentId, setParentDocumentId] = useState('');
  const [applications, setApplications] = useState<{ documentId: string; amount: number }[]>([]);

  // Multi-currency state
  const [currency, setCurrency] = useState<Currency>('PYG');
  const [exchangeRate, setExchangeRate] = useState<number>(1);

  // New Movements State
  const [newMovements, setNewMovements] = useState<{
    id: string;
    accountId: string;
    amount: number;
    date: string;
    description: string;
  }[]>([]);

  // Transactions state
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [allDocuments, setAllDocuments] = useState<ManagedDocument[]>([]);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [showTransactionSelector, setShowTransactionSelector] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredLocatorCode, setRegisteredLocatorCode] = useState('');

  // Contacts Registry
  const [contacts, setContacts] = useState<ManagedContact[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [internalSearch, setInternalSearch] = useState('');
  const [showContactCreator, setShowContactCreator] = useState(false);
  const [newContactTaxId, setNewContactTaxId] = useState('');

  const closingDate = useClosingDate(userId);
  const isLocked = date <= closingDate;
  const isEditLocked = !!(editingDocument && editingDocument.date <= closingDate);

  useEffect(() => {
    const unsub = subscribeToContacts(userId, setContacts);
    const unsubTx = subscribeToTransactions(userId, setAllTransactions);
    const unsubDocs = subscribeToDocuments(userId, setAllDocuments);
    return () => {
      unsub();
      unsubTx();
      unsubDocs();
    };
  }, [userId]);

  useEffect(() => {
    if (editingDocument) {
      setType(editingDocument.type);
      setDate(editingDocument.date);
      setLocatorCode(editingDocument.locatorCode || '');
      setNumber(editingDocument.number || '');
      setTimbrado(editingDocument.timbrado || '');
      setCdc(editingDocument.cdc || '');
      setContactId(editingDocument.contactId || '');
      setContactName(editingDocument.contactName);
      setInternalContactId(editingDocument.internalContactId || '');
      setInternalContactName(editingDocument.internalContactName || '');
      setCondition(editingDocument.condition);
      setObservations(editingDocument.observations || '');
      setTax5Amount(editingDocument.tax5Amount?.toString() || '');
      setTax10Amount(editingDocument.tax10Amount?.toString() || '');
      setItems(editingDocument.items);
      setSelectedTransactionIds(editingDocument.transactionIds || []);
      setIsHistorical(editingDocument.isHistorical || false);
      setHistoricalPendingAmount(editingDocument.historicalPendingAmount?.toString() || '');
      setParentDocumentId(editingDocument.parentDocumentId || '');
      setApplications(editingDocument.applications || []);
      setCurrency(editingDocument.currency || 'PYG');
      setExchangeRate(editingDocument.exchangeRate || 1);
    } else {
      setItems([{
        articleId: '',
        quantity: 1,
        unitPrice: 0,
        total: 0,
        accountingAccountId: ''
      }]);
    }
  }, [editingDocument]);

  useEffect(() => {
    // Si cambian la moneda o el tipo de cambio, actualizamos
    setItems((prevItems) => prevItems.map(item => {
      const i = { ...item };
      if (currency !== 'PYG') {
         const hasForeign = i.foreignUnitPrice != null && i.foreignUnitPrice !== '';
         const fUnitPrice = hasForeign ? Number(i.foreignUnitPrice) : (exchangeRate > 0 ? (Number(i.unitPrice) || 0) / exchangeRate : 0);
         i.foreignUnitPrice = hasForeign ? i.foreignUnitPrice : fUnitPrice; // Preserve string if user typed it
         i.foreignTotal = i.quantity * fUnitPrice;
         i.unitPrice = fUnitPrice * exchangeRate;
         i.total = i.quantity * i.unitPrice;
      } else {
         i.total = i.quantity * (Number(i.unitPrice) || 0);
         i.foreignUnitPrice = undefined;
         i.foreignTotal = undefined;
      }
      return i;
    }));
  }, [currency, exchangeRate]);

  const handleAddItem = () => {
    setItems([...items, {
      articleId: '',
      quantity: 1,
      unitPrice: 0,
      total: 0,
      accountingAccountId: ''
    }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const isReceipt = type === 'RECEIPT_EXPENSE' || type === 'RECEIPT_INCOME';

  const handleItemChange = (index: number, field: keyof DocumentItem, value: any) => {
    const newItems = [...items];
    const item = { ...newItems[index] };

    if (field === 'articleId') {
      const article = articles.find(a => a.id === value);
      if (article) {
        item.accountingAccountId = article.accountingAccountId;
      }
      item.articleId = value;
    } else if (field === 'quantity') {
      item.quantity = value;
    } else if (field === 'foreignUnitPrice') {
      item.foreignUnitPrice = value;
      if (currency === 'PYG') item.unitPrice = Number(value) || 0;
    } else if (field === 'unitPrice') {
      item.unitPrice = value;
      if (currency !== 'PYG') item.foreignUnitPrice = value;
    }

    if (currency !== 'PYG') {
      const fUP = Number(item.foreignUnitPrice) || 0;
      item.foreignTotal = item.quantity * fUP;
      item.unitPrice = fUP * exchangeRate;
      item.total = item.quantity * item.unitPrice;
    } else {
      const up = Number(item.unitPrice) || 0;
      item.unitPrice = up;
      item.total = item.quantity * up;
      item.foreignUnitPrice = undefined;
      item.foreignTotal = undefined;
    }
    
    newItems[index] = item;
    setItems(newItems);
  };

  const transactionBalances = React.useMemo(() => {
    // We compute the true available balances WITHOUT the current document's consumption,
    // so we can see how much is truly "available" to this document being edited.
    const docsExcludingCurrent = allDocuments.filter(d => d.id !== editingDocument?.id);
    return calculateTransactionBalances(allTransactions, docsExcludingCurrent);
  }, [allTransactions, allDocuments, editingDocument?.id]);

  const getAvailableTransactionAmount = (tId: string) => {
    const tInfo = transactionBalances[tId];
    if (!tInfo) return 0;
    return tInfo.availableAmount;
  };

  const isTransactionAvailable = (tId: string) => {
    return getAvailableTransactionAmount(tId) > 1; // 1 guaraní de margen
  };

  const selectedTransactions = allTransactions.filter(t => selectedTransactionIds.includes(t.id));
  const linkedTransactionsTotal = selectedTransactions.reduce((acc, t) => {
    return acc + getAvailableTransactionAmount(t.id);
  }, 0);
  const newMovementsTotal = newMovements.reduce((acc, m) => acc + m.amount, 0);
  const transactionsTotal = linkedTransactionsTotal + newMovementsTotal;

  const totalAmount = isReceipt
    ? Math.max(
        applications.reduce((acc, curr) => acc + (curr.amount || 0), 0), 
        (editingDocument?.totalAmount || 0),
        (applications.length === 0 ? transactionsTotal : 0)
      )
    : items.reduce((acc, curr) => acc + curr.total, 0);

  const executeDelete = async () => {
    if (!editingDocument) return;
    setLoading(true);
    try {
      await deleteManagedDocument(userId, editingDocument.id);
      onSuccess();
    } catch (error) {
      console.error(error);
      alert("No se pudo eliminar el documento. Es posible que haya otros datos vinculados que lo impidan.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validations
    if (!contactName || !internalContactName) {
       alert("Por favor completa el cliente/proveedor y el emisor/receptor interno.");
       return;
    }

    if (!isReceipt && items.some(i => !i.articleId)) {
       alert("Por favor completa todos los artículos.");
       return;
    }

    if (isReceipt && applications.length === 0) {
      alert("Por favor selecciona al menos un documento para aplicar este recibo.");
      return;
    }

    if (isReceipt && totalAmount <= 0) {
      alert("Por favor ingresa un monto válido para el recibo (aplica montos a las facturas seleccionadas).");
      return;
    }

    if (condition === 'CONTADO' || isReceipt) {
      if (isHistorical) {
        alert("Un documento histórico (saldo inicial) debe ser a CRÉDITO.");
        return;
      }
      if (selectedTransactionIds.length === 0 && newMovements.length === 0) {
        alert("Para documentos al contado es obligatorio vincular al menos un movimiento financiero.");
        return;
      }
      if (transactionsTotal < totalAmount) {
        alert(`La suma de movimientos financieros (${formatCurrency(transactionsTotal)}) es insuficiente para cubrir el total (${formatCurrency(totalAmount)}).`);
        return;
      }
    }

    if (isHistorical && (!historicalPendingAmount || parseFloat(historicalPendingAmount) <= 0)) {
      alert("Para carga histórica, debes ingresar un saldo pendiente válido.");
      return;
    }

    if ((type === 'PURCHASE_INVOICE' || type === 'SALES_INVOICE') && timbrado && number) {
      const isDuplicate = allDocuments.some(d => {
        if (editingDocument && d.id === editingDocument.id) return false;
        if (d.type !== type) return false;
        if (d.number !== number || d.timbrado !== timbrado) return false;
        return true;
      });

      if (isDuplicate) {
        alert("Ya existe una factura registrada con este mismo Nro. de Timbrado y Nro. de Documento.");
        return;
      }
    }

    setLoading(true);
    try {
      const isNew = !editingDocument;
      let refCode = locatorCode.trim().toUpperCase();
      if (!refCode && isNew) {
         refCode = await generateUniqueLocatorCode(userId); // fallback although UI usually won't leave it blank if they want it random
      }

      const hPending = isHistorical ? parseFloat(historicalPendingAmount) : 0;

      // 4. Distribuir total del documento entre los movimientos (Nuevos y Vinculados) SOLO SI ES CONTADO
      let remainingDocTotal = totalAmount;
      let finalTransactionIds = [...selectedTransactionIds];
      let newTransactionsData: any[] = [];
      const isExpense = type === 'PURCHASE_INVOICE' || type === 'INTERNAL_EXPENSE' || type === 'RECEIPT_EXPENSE';

      if (condition !== 'CREDITO' && !isHistorical) {
        // Primero descontamos lo que cubren los movimientos NUEVOS (que son 1-a-1)
        const newMovementsTotalForDoc = newMovements.reduce((sum, m) => sum + m.amount, 0);
        remainingDocTotal -= newMovementsTotalForDoc;

        // Luego distribuimos el resto entre los movimientos VINCULADOS existentes (que pueden ser compartidos)
        for (const tId of selectedTransactionIds) {
          if (remainingDocTotal <= 1) break;
          const t = allTransactions.find((tx: any) => tx.id === tId);
          if (!t) continue;
          
          // Calculamos saldo disponible REAL de esta transacción
          const available = getAvailableTransactionAmount(tId);
          const toAssign = Math.min(available, remainingDocTotal);
          
          if (toAssign > 0) {
            remainingDocTotal -= toAssign;
          }
        }

        newTransactionsData = newMovements.map(m => ({
          description: m.description || `${type.replace('_', ' ')}: ${contactName} - ${number || locatorCode}`,
          type: isExpense ? 'EXPENSE' as TransactionType : 'INCOME' as TransactionType,
          date: m.date,
          details: [
            { 
              accountId: m.accountId, 
              amount: m.amount, 
              type: isExpense ? 'DEBIT' as const : 'CREDIT' as const 
            }
          ]
        }));
      } else {
        // Para créditos, no se vinculan movimientos directos, se pagan vía Recibo aparte.
        finalTransactionIds = [];
      }

      const docData: Omit<ManagedDocument, 'id' | 'createdAt' | 'userId'> = {
        type,
        date,
        number,
        timbrado: timbrado || undefined,
        cdc: cdc || undefined,
        locatorCode: refCode,
        contactId: contactId || undefined,
        contactName,
        internalContactId: internalContactId || undefined,
        internalContactName,
        condition,
        totalAmount,
        currency,
        exchangeRate,
        foreignTotalAmount: currency !== 'PYG' ? (isReceipt ? totalAmount / exchangeRate : items.reduce((acc, curr) => acc + (Number(curr.foreignTotal) || 0), 0)) : undefined,
        tax5Amount: tax5Amount ? parseFloat(tax5Amount) : undefined,
        tax10Amount: tax10Amount ? parseFloat(tax10Amount) : undefined,
        observations,
        items: isReceipt ? [] : items.map(i => ({
          ...i,
          quantity: Number(i.quantity) || 0,
          unitPrice: Number(i.unitPrice) || 0,
          foreignUnitPrice: i.foreignUnitPrice !== undefined ? Number(i.foreignUnitPrice) : undefined,
          total: Number(i.total) || 0,
          foreignTotal: i.foreignTotal !== undefined ? Number(i.foreignTotal) : undefined
        })),
        transactionIds: finalTransactionIds,
        parentDocumentId: isReceipt ? (applications[0]?.documentId || undefined) : undefined,
        applications: isReceipt ? applications : undefined,
        appliedDocumentIds: isReceipt ? applications.map(a => a.documentId) : undefined,
        remainingAmount: isReceipt ? 0 : (isHistorical ? hPending : (condition === 'CREDITO' ? (totalAmount - transactionsTotal) : 0)),
        isCancelled: isReceipt ? true : (!isHistorical && (condition === 'CONTADO' || (totalAmount - transactionsTotal <= 0))),
        isHistorical,
        historicalPendingAmount: isHistorical ? hPending : undefined
      };

      if (editingDocument) {
        await updateManagedDocument(userId, editingDocument.id, docData, newTransactionsData);
        onSuccess();
      } else {
        await createManagedDocument(userId, docData, newTransactionsData);
        setRegisteredLocatorCode(docData.locatorCode || '');
        setShowSuccessModal(true);
      }
    } catch (error: any) {
      console.error("Error saving document:", error);
      alert("Error al guardar el documento.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-start justify-center p-4 overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-4xl my-8 overflow-hidden shadow-2xl"
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between bg-zinc-800/20">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {editingDocument ? 'Editar' : 'Nuevo'} {type.replace('_', ' ')}
              {isHistorical && <span className="text-[10px] bg-indigo-500 text-white px-2 py-0.5 rounded-md uppercase font-black tracking-widest">Histórico</span>}
            </h2>
            <p className="text-xs text-zinc-500">Carga los detalles del documento comercial.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-xl transition-all">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {isLocked && (
            <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              <Lock className="text-red-400 shrink-0" size={20} />
              <div className="space-y-1">
                <p className="text-sm font-bold text-red-500">Documento Bloqueado</p>
                <p className="text-xs text-red-400/80 leading-relaxed">
                  La fecha corresponde a un periodo cerrado.
                  {editingDocument ? ' No puedes editar ni eliminar este documento.' : ' No puedes crear documentos en este periodo.'}
                </p>
              </div>
            </div>
          )}
          
          {/* Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                <Calendar size={12} /> Fecha
              </label>
              <input 
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full bg-zinc-950 border ${isLocked ? 'border-red-500/30' : 'border-zinc-800'} rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all`}
                disabled={isEditLocked}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                <Hash size={12} /> Nro. Comprobante
              </label>
              <input 
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Ej: 001-001-000123"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                disabled={isEditLocked}
              />
            </div>

            <div className="space-y-2 relative">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center justify-between">
                <span className="flex items-center gap-2"><User size={12} /> Cliente / Proveedor</span>
                <button 
                  type="button"
                  onClick={() => setShowContactCreator(true)}
                  className="text-emerald-500 hover:text-emerald-400 font-bold flex items-center gap-1"
                >
                  <Plus size={10} /> Nuevo
                </button>
              </label>
              
              <div className="relative">
                <input 
                  required={type.includes('INVOICE')}
                  value={contactSearch || contactName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setContactSearch(val);
                    setContactName(val);
                    
                    // Search by taxId automatically
                    const found = contacts.find(c => c.taxId === val);
                    if (found) {
                      setContactId(found.id);
                      setContactName(found.name);
                      setContactSearch(found.name);
                    } else if (contactId) {
                      setContactId('');
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setContactSearch(''), 200);
                  }}
                  placeholder="Nombre o Nro. Identificación (RUC/CI)"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                  disabled={isEditLocked}
                />

                <AnimatePresence>
                  {contactSearch.length > 0 && !contactId && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-20 max-h-48 overflow-y-auto"
                    >
                      {contacts
                        .filter(c => 
                          (type.includes('PURCHASE') || type.includes('EXPENSE') ? c.isProvider : c.isCustomer) &&
                          (c.name.toLowerCase().includes(contactSearch.toLowerCase()) || c.taxId.includes(contactSearch))
                        )
                        .map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setContactId(c.id);
                              setContactName(c.name);
                              setContactSearch('');
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-zinc-800 flex items-center justify-between group transition-colors border-b border-zinc-800 last:border-0"
                          >
                            <div>
                              <div className="text-sm font-bold text-white group-hover:text-emerald-400">{c.name}</div>
                              <div className="text-[10px] text-zinc-500 uppercase tracking-tighter">{c.taxIdType}: {c.taxId}</div>
                            </div>
                            <ArrowRight size={14} className="text-zinc-600 group-hover:text-emerald-400" />
                          </button>
                        ))
                      }
                      <button
                        type="button"
                        onClick={() => {
                          setNewContactTaxId(contactSearch);
                          setShowContactCreator(true);
                          setContactSearch('');
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-zinc-800 text-emerald-500 text-xs font-bold"
                      >
                        + Crear "{contactSearch}" como nuevo contacto
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="space-y-2 relative">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Building2 size={12} /> 
                  {type.includes('PURCHASE') || type.includes('EXPENSE') ? 'Recibido Por (Interno)' : 'Emitido Por (Interno)'}
                </span>
              </label>
              
              <div className="relative">
                <input 
                  required={type.includes('INVOICE')}
                  value={internalSearch || internalContactName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInternalSearch(val);
                    setInternalContactName(val);
                    
                    const found = contacts.find(c => c.taxId === val);
                    if (found) {
                      setInternalContactId(found.id);
                      setInternalContactName(found.name);
                      setInternalSearch(found.name);
                    } else if (internalContactId) {
                      setInternalContactId('');
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setInternalSearch(''), 200);
                  }}
                  placeholder="Nombre o ID del responsable interno..."
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all placeholder:text-zinc-700"
                  disabled={isEditLocked}
                />

                <AnimatePresence>
                  {internalSearch.length > 0 && !internalContactId && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-20 max-h-48 overflow-y-auto"
                    >
                      {contacts
                        .filter(c => 
                          // Internal party can be any relevant contact, usually employees or self-entities
                          c.name.toLowerCase().includes(internalSearch.toLowerCase()) || c.taxId.includes(internalSearch)
                        )
                        .map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setInternalContactId(c.id);
                              setInternalContactName(c.name);
                              setInternalSearch('');
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-zinc-800 flex items-center justify-between group transition-colors border-b border-zinc-800 last:border-0"
                          >
                            <div>
                              <div className="text-sm font-bold text-white group-hover:text-emerald-400">{c.name}</div>
                              <div className="text-[10px] text-zinc-500 uppercase tracking-tighter">{c.taxIdType}: {c.taxId}</div>
                            </div>
                            <ArrowRight size={14} className="text-zinc-600 group-hover:text-emerald-400" />
                          </button>
                        ))
                      }
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {!isReceipt && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                  <CreditCard size={12} /> Condición
                </label>
                <div className="grid grid-cols-2 gap-2 p-1 bg-zinc-950 border border-zinc-800 rounded-xl">
                  <button
                    type="button"
                    onClick={() => { if (!isEditLocked) setCondition('CONTADO') }}
                    disabled={isEditLocked}
                    className={`py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${condition === 'CONTADO' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                  >
                    CONTADO
                  </button>
                  <button
                    type="button"
                    onClick={() => { if (!isEditLocked) setCondition('CREDITO') }}
                    disabled={isEditLocked}
                    className={`py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${condition === 'CREDITO' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                  >
                    CRÉDITO
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                Moneda
              </label>
              <select
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value as Currency);
                  if (e.target.value === 'PYG') setExchangeRate(1);
                }}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
              >
                <option value="PYG">PYG - Guaraníes</option>
                <option value="USD">USD - Dólares Americanos</option>
                <option value="ARS">ARS - Pesos Argentinos</option>
                <option value="BRL">BRL - Reales Brasileños</option>
              </select>
            </div>

            {currency !== 'PYG' && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-amber-500 uppercase tracking-widest px-1 flex items-center gap-2">
                  Tipo de Cambio a PYG
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={exchangeRate || ''}
                  onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 1)}
                  className="w-full bg-amber-500/10 border border-amber-500/30 text-amber-500 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-500/50 outline-none transition-all"
                  disabled={isEditLocked}
                />
              </div>
            )}

            {isReceipt && (
              <div className="space-y-4 md:col-span-3 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest px-1 flex items-center gap-2">
                    <FileSearch size={12} /> Facturas / Documentos Pendientes {contactName ? `de ${contactName}` : ''}
                  </label>
                  {contactId && (
                    <button 
                      type="button"
                      onClick={() => {
                        setContactId('');
                        setContactName('');
                        setApplications([]);
                      }}
                      className="text-[10px] font-bold text-zinc-500 hover:text-white uppercase transition-colors"
                    >
                      Limpiar Cliente/Proveedor
                    </button>
                  )}
                </div>

                {!contactId && applications.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-8 border-2 border-dashed border-emerald-500/10 rounded-2xl bg-emerald-500/5">
                      <FileSearch size={32} className="mx-auto text-emerald-500/20 mb-2" />
                      <p className="text-xs text-zinc-500 max-w-xs mx-auto px-4">Selecciona un <b>Cliente/Proveedor</b> arriba o busca una <b>Factura Específica</b> para empezar.</p>
                    </div>
                    
                    <div className="space-y-2">
                       <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest px-1">Búsqueda Rápida de Factura Pendiente</label>
                       <select
                        value=""
                        onChange={(e) => {
                          const docId = e.target.value;
                          const d = allDocuments.find(doc => doc.id === docId);
                          if (d) {
                            if (!contactId && d.contactId) {
                               setContactId(d.contactId);
                               setContactName(d.contactName || '');
                            }
                            if (!applications.find(a => a.documentId === d.id)) {
                               setApplications(prev => [...prev, { documentId: d.id, amount: d.remainingAmount }]);
                            }
                          }
                        }}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                      >
                        <option value="">Buscar factura por número o saldo...</option>
                        {allDocuments
                          .filter(d => {
                            const isCorrectDirection = type === 'RECEIPT_EXPENSE' 
                              ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                              : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                            
                            return (d.condition === 'CREDITO' || d.isHistorical) && d.remainingAmount > 0 && isCorrectDirection;
                          })
                          .map(d => (
                            <option key={`qs-${d.id}`} value={d.id}>
                              {d.number || d.locatorCode} ({d.contactName}) - Saldo: {formatCurrency(d.remainingAmount)}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="max-h-64 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                      {allDocuments
                        .filter(d => {
                          const isCorrectDirection = type === 'RECEIPT_EXPENSE' 
                            ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                            : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                          
                          const originallyAppliedAmount = editingDocument?.applications?.find(a => a.documentId === d.id)?.amount || 0;
                          const trueRemainingAmount = d.remainingAmount + originallyAppliedAmount;
                          
                          const isMatchDirectionAndAmount = (d.condition === 'CREDITO' || d.isHistorical) && trueRemainingAmount > 0 && isCorrectDirection;
                          const isApplied = applications.some(a => a.documentId === d.id);
                          const isMatchingContact = contactId ? d.contactId === contactId : false;

                          return isApplied || (isMatchDirectionAndAmount && isMatchingContact);
                        })
                        .map(d => {
                          const app = applications.find(a => a.documentId === d.id);
                          const isSelected = !!app;
                          const originallyAppliedAmount = editingDocument?.applications?.find(a => a.documentId === d.id)?.amount || 0;
                          const trueRemainingAmount = d.remainingAmount + originallyAppliedAmount;

                          return (
                            <div 
                              key={d.id} 
                              className={`p-3 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center gap-3 ${
                                isSelected ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-950/50 border-zinc-800'
                              }`}
                            >
                              <div className="flex-1 flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setApplications(prev => prev.filter(a => a.documentId !== d.id));
                                    } else {
                                      setApplications(prev => [...prev, { documentId: d.id, amount: trueRemainingAmount }]);
                                    }
                                  }}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all min-w-[2rem] ${
                                    isSelected ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                                  }`}
                                >
                                  {isSelected ? <CheckCircle2 size={16} /> : <Plus size={16} />}
                                </button>
                                <div>
                                  <div className="text-xs font-bold text-white">{d.number || d.locatorCode}</div>
                                  <div className="text-[10px] text-zinc-500">{d.date} • {d.contactName} • Saldo Total: {formatCurrency(trueRemainingAmount)}</div>
                                </div>
                              </div>

                              {isSelected && (
                                <div className="flex items-center gap-2 mt-2 md:mt-0">
                                  <label className="text-[10px] font-bold text-emerald-500 uppercase">Gasto a Aplicar:</label>
                                  <div className="relative w-full md:w-32">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-zinc-500">Gs.</span>
                                    <input 
                                      type="number"
                                      value={app?.amount || 0}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        setApplications(prev => prev.map(a => a.documentId === d.id ? { ...a, amount: Math.min(val, trueRemainingAmount) } : a));
                                      }}
                                      className="w-full bg-zinc-900 border border-emerald-500/20 rounded-lg pl-7 pr-2 py-1.5 text-xs font-mono font-bold text-emerald-400"
                                    />
                                  </div>
                                  <button 
                                    type="button"
                                    onClick={() => setApplications(prev => prev.map(a => a.documentId === d.id ? { ...a, amount: trueRemainingAmount } : a))}
                                    className="text-[9px] font-black text-indigo-400 hover:text-indigo-300 uppercase tracking-tighter"
                                  >
                                    Máx.
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })
                      }
                      
                      {allDocuments.filter(d => {
                          const isCorrectDirection = type === 'RECEIPT_EXPENSE' 
                            ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                            : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                          
                          const originallyAppliedAmount = editingDocument?.applications?.find(a => a.documentId === d.id)?.amount || 0;
                          const trueRemainingAmount = d.remainingAmount + originallyAppliedAmount;
                          
                          const isMatchDirectionAndAmount = (d.condition === 'CREDITO' || d.isHistorical) && trueRemainingAmount > 0 && isCorrectDirection;
                          const isApplied = applications.some(a => a.documentId === d.id);
                          const isMatchingContact = contactId ? d.contactId === contactId : false;

                          return isApplied || (isMatchDirectionAndAmount && isMatchingContact);
                      }).length === 0 && (
                        <div className="text-center py-8 bg-zinc-950/20 border border-zinc-800 rounded-2xl border-dashed">
                          <p className="text-xs text-zinc-500 italic">No hay facturas pendientes mostrando actualmente.</p>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-zinc-800/50 flex flex-col md:flex-row items-center justify-between gap-4">
                      {/* Manual Search Addition */}
                      <div className="w-full md:w-1/2">
                        <select
                          value=""
                          onChange={(e) => {
                            const docId = e.target.value;
                            const d = allDocuments.find(doc => doc.id === docId);
                            if (d && !applications.find(a => a.documentId === d.id)) {
                              setApplications(prev => [...prev, { documentId: d.id, amount: d.remainingAmount }]);
                            }
                          }}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-[10px] text-zinc-400 focus:outline-none focus:border-emerald-500/50"
                        >
                          <option value="">+ Agregar factura de otro contacto...</option>
                          {allDocuments
                            .filter(d => {
                              const isCorrectDirection = type === 'RECEIPT_EXPENSE' 
                                ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                                : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                              return (d.condition === 'CREDITO' || d.isHistorical) && d.remainingAmount > 0 && isCorrectDirection && !applications.find(a => a.documentId === d.id);
                            })
                            .map(d => (
                              <option key={`add-${d.id}`} value={d.id}>
                                {d.contactName} - {d.number || d.locatorCode} (Saldo: {formatCurrency(d.remainingAmount)})
                              </option>
                            ))
                          }
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-4 text-right">
                        <div className="text-[10px] font-bold text-zinc-500 uppercase">Documentos Aplicados: {applications.length}</div>
                        <div className="text-sm font-black text-emerald-500">Total Recibo: {formatCurrency(totalAmount)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                <Hash size={12} /> Nro. Timbrado
              </label>
              <input 
                value={timbrado}
                onChange={(e) => setTimbrado(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="8 dígitos (Opcional)"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all placeholder:text-zinc-700"
                disabled={isEditLocked}
              />
              <p className="text-[9px] text-zinc-600 px-1 italic">8 dígitos numéricos.</p>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                <FileSearch size={12} /> Código de Referencia
                <button 
                  type="button"
                  onClick={async () => {
                     try {
                       const code = await generateUniqueLocatorCode(userId);
                       setLocatorCode(code);
                     } catch (e) {
                       console.error(e);
                     }
                  }}
                  className="text-indigo-400 hover:text-indigo-300 ml-auto flex items-center justify-center rounded hover:bg-zinc-800 p-0.5"
                  title="Generar Código Aleatorio"
                >
                  <PlusCircle size={12} />
                </button>
              </label>
                <input 
                  value={locatorCode}
                  onChange={(e) => setLocatorCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5))}
                  placeholder="Ej. AB123 (Opcional)"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all placeholder:text-zinc-700 font-mono tracking-widest"
                  maxLength={5}
                  disabled={isEditLocked}
                />
              <p className="text-[9px] text-zinc-600 px-1 italic">2 Letras y 3 Números.</p>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1 flex items-center gap-2">
                <FileSearch size={12} /> CDC (Código de Control)
              </label>
              <input 
                value={cdc}
                onChange={(e) => setCdc(e.target.value.replace(/\D/g, '').slice(0, 44))}
                placeholder="44 dígitos (Opcional)"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all placeholder:text-zinc-700"
                disabled={isEditLocked}
              />
              <p className="text-[9px] text-zinc-600 px-1 italic">44 dígitos.</p>
            </div>

            <div className="md:col-span-3 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                    <Clock size={20} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white">Carga Histórica / Saldo Inicial</h4>
                    <p className="text-[10px] text-zinc-500">Marca esta opción si la factura es anterior al inicio del sistema.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (isEditLocked) return;
                    setIsHistorical(!isHistorical);
                    if (!isHistorical) {
                      setCondition('CREDITO');
                    }
                  }}
                  className={`w-12 h-6 rounded-full transition-all relative disabled:opacity-50 ${isHistorical ? 'bg-indigo-500' : 'bg-zinc-700'}`}
                  disabled={isEditLocked}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${isHistorical ? 'right-1' : 'left-1'}`} />
                </button>
              </div>

              {isHistorical && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-1">Saldo Pendiente Actual</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-bold">Gs.</span>
                      <input 
                        type="number"
                        required
                        value={historicalPendingAmount}
                        onChange={(e) => setHistoricalPendingAmount(e.target.value)}
                        placeholder="Ej: 500000"
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-12 pr-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all"
                        disabled={isEditLocked}
                      />
                    </div>
                    <p className="text-[9px] text-zinc-500 px-1 italic">Este será el monto que figurará como deuda para cobrar/pagar.</p>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                    <Info size={16} className="text-indigo-400 shrink-0" />
                    <p className="text-[10px] text-indigo-400 leading-snug font-medium">
                      Al cargar como histórico, podrás generar un <b>Asiento de Apertura</b> basado únicamente en el saldo pendiente, sin afectar tu inventario o flujo de caja anterior.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className={`space-y-2 md:col-span-3 transition-opacity duration-300 ${(isHistorical || condition === 'CREDITO') ? 'opacity-30 pointer-events-none hidden' : 'opacity-100'}`}>
              <div className="flex items-center justify-between px-1">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                  <Search size={12} /> Movimientos Financieros Vinculados
                  {condition === 'CONTADO' && <span className="text-red-500 lowercase font-normal">(obligatorio para contado)</span>}
                </label>
                <div className="text-[10px] font-bold">
                  Total Vinculado: <span className={transactionsTotal === totalAmount ? 'text-emerald-500' : 'text-amber-500'}>{formatCurrency(transactionsTotal)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {selectedTransactions.map(t => (
                    <div key={t.id} className="bg-zinc-800 border border-zinc-700 px-3 py-2 rounded-xl flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-white leading-tight">{t.description}</span>
                        <span className="text-[9px] text-zinc-500 leading-tight">{t.date} • {formatCurrency(t.details[0]?.amount || 0)}</span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => setSelectedTransactionIds(prev => prev.filter(id => id !== t.id))}
                        className="text-zinc-500 hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                  {newMovements.map((m, mIdx) => (
                    <div key={m.id} className="bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 rounded-xl flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold text-emerald-400 leading-tight text-nowrap">
                          {accounts.find(a => a.id === m.accountId)?.name || 'Cta. Desconocida'}
                        </span>
                        <span className="text-[9px] text-emerald-500/60 leading-tight">{m.date} • {formatCurrency(m.amount)}</span>
                      </div>
                      <button 
                        type="button"
                        onClick={() => setNewMovements(prev => prev.filter((_, i) => i !== mIdx))}
                        className="text-emerald-500 hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                       const amountLeft = totalAmount - transactionsTotal;
                       setNewMovements([...newMovements, {
                         id: Math.random().toString(36).substr(2, 9),
                         accountId: accounts[0]?.id || '',
                         amount: amountLeft > 0 ? amountLeft : 0,
                         date: date,
                         description: ''
                       }]);
                    }}
                    className="h-10 px-4 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-xl text-xs font-bold text-emerald-500 transition-all flex items-center gap-2"
                  >
                    <PlusCircle size={14} /> Registrar Pago/Cobro
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowTransactionSelector(true)}
                    className="h-10 px-4 bg-zinc-950 border border-zinc-800 border-dashed hover:border-emerald-500/50 hover:bg-emerald-500/5 rounded-xl text-xs font-bold text-zinc-500 hover:text-emerald-500 transition-all flex items-center gap-2"
                  >
                    <Search size={14} /> Vincular Existente
                  </button>
                </div>

                {newMovements.length > 0 && (
                   <div className="space-y-2 py-2 bg-zinc-900/50 rounded-2xl p-4 border border-zinc-800">
                     <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Detalle de Nuevos Movimientos</p>
                     {newMovements.map((m, idx) => (
                       <div key={m.id} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                         <div className="md:col-span-2">
                           <select 
                             value={m.accountId}
                             onChange={(e) => {
                               const updated = [...newMovements];
                               updated[idx].accountId = e.target.value;
                               setNewMovements(updated);
                             }}
                             className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-white"
                           >
                             {accounts.map(a => {
                               const institution = a.institutionId ? getInstitutionById(a.institutionId) : null;
                               const instName = institution ? ` - ${institution.name}` : '';
                               return <option key={a.id} value={a.id}>{a.name}{instName} ({formatCurrency(a.balance)})</option>;
                             })}
                           </select>
                         </div>
                         <div>
                            <input 
                              type="number"
                              value={m.amount}
                              onChange={(e) => {
                                const updated = [...newMovements];
                                updated[idx].amount = parseFloat(e.target.value) || 0;
                                setNewMovements(updated);
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-right font-bold text-emerald-400"
                            />
                         </div>
                         <div>
                            <input 
                              type="date"
                              value={m.date}
                              onChange={(e) => {
                                const updated = [...newMovements];
                                updated[idx].date = e.target.value;
                                setNewMovements(updated);
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-white"
                            />
                         </div>
                       </div>
                     ))}
                   </div>
                )}

                {condition === 'CONTADO' && transactionsTotal !== totalAmount && (
                  <div className={`flex items-center gap-2 text-[10px] font-bold p-3 rounded-xl border ${
                    transactionsTotal < totalAmount 
                      ? 'bg-amber-500/10 border-amber-500/20 text-amber-500' 
                      : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400'
                  }`}>
                    {transactionsTotal < totalAmount ? <AlertCircle size={14} /> : <Info size={14} />}
                    {transactionsTotal < totalAmount 
                      ? `Faltan ${formatCurrency(totalAmount - transactionsTotal)} para cubrir el total.` 
                      : `Se utilizarán ${formatCurrency(totalAmount)} de los movimientos seleccionados. El resto (Gs. ${formatCurrency(transactionsTotal - totalAmount)}) quedará disponible.`
                    }
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Items Table */}
          {!isReceipt ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <Package size={16} className="text-emerald-500" /> Detalle de Artículos
                </h3>
                <button 
                  type="button" 
                  onClick={handleAddItem}
                  className="text-xs text-emerald-500 hover:text-emerald-400 font-bold flex items-center gap-1 transition-colors"
                >
                  <Plus size={14} /> Agregar Item
                </button>
              </div>

              <div className="space-y-4">
                {items.map((item, idx) => (
                  <div key={idx} className="flex flex-col md:flex-row gap-4 p-4 bg-zinc-950/50 border border-zinc-800 rounded-2xl relative group">
                    <div className="flex-1 space-y-1">
                      <label className="text-[9px] font-bold text-zinc-600 uppercase px-1">Artículo / Servicio</label>
                      <select
                        required
                        value={item.articleId}
                        onChange={(e) => handleItemChange(idx, 'articleId', e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                      >
                        <option value="">Seleccionar artículo...</option>
                        {articles.map(art => (
                          <option key={art.id} value={art.id}>{art.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="w-full md:w-24 space-y-1">
                      <label className="text-[9px] font-bold text-zinc-600 uppercase px-1">Cant.</label>
                      <input 
                        type="number"
                        required
                        min="0.0001"
                        step="any"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-center"
                      />
                    </div>

                    <div className="flex-1 space-y-1">
                      <label className="text-[9px] font-bold text-zinc-600 uppercase px-1">
                        Precio Unitario {currency !== 'PYG' ? `(${currency})` : ''}
                      </label>
                      <input 
                        type="number"
                        required
                        step="any"
                        value={currency !== 'PYG' ? (item.foreignUnitPrice ?? 0) : item.unitPrice}
                        onChange={(e) => handleItemChange(idx, currency !== 'PYG' ? 'foreignUnitPrice' : 'unitPrice', e.target.value)}
                        className={`w-full bg-zinc-900 border ${currency !== 'PYG' ? 'border-amber-500/30 text-amber-500' : 'border-zinc-800'} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-right`}
                      />
                    </div>

                    <div className="flex-1 space-y-1">
                      <label className="text-[9px] font-bold text-zinc-600 uppercase px-1">Total {currency !== 'PYG' ? `(${currency})` : ''}</label>
                      <div className="w-full bg-zinc-900/30 border border-transparent px-3 py-2 text-sm font-bold text-white text-right flex flex-col justify-end">
                        <span>{currency !== 'PYG' ? new Intl.NumberFormat('es-PY', { style: 'currency', currency: currency }).format(item.foreignTotal || 0) : formatCurrency(item.total)}</span>
                        {currency !== 'PYG' && <span className="text-[9px] text-zinc-500 mt-1">{formatCurrency(item.total)} (PYG)</span>}
                      </div>
                    </div>

                    <div className="flex items-end pb-1">
                      <button 
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        disabled={items.length === 1}
                        className="p-2 text-zinc-600 hover:text-red-500 disabled:opacity-0 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            null
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-zinc-800">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Observaciones</label>
                <textarea 
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all resize-none"
                  rows={3}
                  placeholder="Datos adicionales del documento..."
                />
              </div>
            </div>

            <div className="bg-zinc-950/50 p-6 rounded-3xl border border-zinc-800 space-y-4">
              {!isReceipt && (
                <div className="grid grid-cols-2 gap-4 pb-4 border-b border-zinc-900">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">IVA 5% (EST.)</label>
                    <input 
                      type="number"
                      value={tax5Amount}
                      onChange={(e) => setTax5Amount(e.target.value)}
                      placeholder="Gs. 0"
                      className="w-full bg-zinc-950 border border-zinc-900 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-right font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">IVA 10% (EST.)</label>
                    <input 
                      type="number"
                      value={tax10Amount}
                      onChange={(e) => setTax10Amount(e.target.value)}
                      placeholder="Gs. 0"
                      className="w-full bg-zinc-950 border border-zinc-900 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-right font-mono"
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center text-zinc-400">
                <span className="text-sm">Neto Gravado</span>
                <span className="text-sm font-medium">{formatCurrency(totalAmount - (parseFloat(tax5Amount || '0') + parseFloat(tax10Amount || '0')))}</span>
              </div>
              <div className="flex justify-between items-center text-zinc-400 border-b border-zinc-900 pb-4">
                <span className="text-sm">Impuestos (5% + 10%)</span>
                <span className="text-sm font-medium">{formatCurrency(parseFloat(tax5Amount || '0') + parseFloat(tax10Amount || '0'))}</span>
              </div>
              <div className="flex justify-between items-center bg-emerald-500/10 p-4 rounded-2xl">
                <span className="text-lg font-bold text-white">TOTAL</span>
                <span className="text-2xl font-black text-emerald-500">{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>

          <div className="pt-8 flex flex-col md:flex-row gap-4">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-2xl transition-all"
            >
              {isLocked ? 'Cerrar' : 'Cancelar'}
            </button>
            {editingDocument && !isLocked && (
               <button 
                 type="button"
                 onClick={() => setShowDeleteConfirm(true)}
                 disabled={loading}
                 className="flex-1 py-4 bg-red-600/20 hover:bg-red-600/40 text-red-500 font-bold rounded-2xl transition-all flex items-center justify-center gap-2"
               >
                 <Trash2 size={20} />
                 Eliminar
               </button>
            )}
            {!isLocked && (
              <button 
                type="submit"
                disabled={loading}
                className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 size={24} className="animate-spin" /> : (
                  <>
                    <Save size={24} />
                    {editingDocument ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </motion.div>

      <AnimatePresence>
        {showTransactionSelector && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
             <motion.div 
               initial={{ scale: 0.9, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl"
             >
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between bg-zinc-800/20">
                  <div>
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <FileSearch size={18} className="text-emerald-500" />
                      Seleccionar Movimientos Financieros
                    </h3>
                    <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">Busca y selecciona los pagos vinculados a esta factura</p>
                  </div>
                  <button onClick={() => setShowTransactionSelector(false)} className="p-2 hover:bg-zinc-800 rounded-xl transition-all"><X size={20}/></button>
                </div>
                
                <div className="p-6 space-y-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 text-zinc-600" size={18} />
                    <input 
                      autoFocus
                      value={transactionSearch}
                      onChange={e => setTransactionSearch(e.target.value)}
                      placeholder="Buscar por descripción, fecha o importe..."
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                    />
                  </div>

                  <div className="max-h-96 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {allTransactions
                      .filter(t => {
                        // Filter by search text
                        const searchLower = transactionSearch.toLowerCase();
                        const matchesSearch = t.description.toLowerCase().includes(searchLower) || 
                                           t.date.includes(searchLower) ||
                                           (t.details[0]?.amount.toString().includes(searchLower));
                        
                        // Valid types for purchase: EXPENSE. For sales: INCOME.
                        const isValidType = (type.includes('PURCHASE') || type.includes('EXPENSE')) 
                          ? (t.type === 'EXPENSE')
                          : (t.type === 'INCOME');

                        // EXCLUSION LOGIC: Filter out transactions that don't have enough balance left.
                        const isAvailable = isTransactionAvailable(t.id);
                        const isCurrentlySelected = selectedTransactionIds.includes(t.id);

                        return matchesSearch && isValidType && (isAvailable || isCurrentlySelected);
                      })
                      .map(t => {
                        const isSelected = selectedTransactionIds.includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                              setSelectedTransactionIds(prev => 
                                isSelected ? prev.filter(id => id !== t.id) : [...prev, t.id]
                              );
                            }}
                            className={`w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between group ${isSelected ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-zinc-950/50 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:bg-zinc-900'}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-emerald-500/20 text-emerald-500' : 'bg-zinc-800 text-zinc-600'}`}>
                                {isSelected ? <CheckCircle2 size={20} /> : <ArrowRight size={20} />}
                              </div>
                              <div>
                                <div className={`text-sm font-bold ${isSelected ? 'text-emerald-400' : 'text-white'}`}>{t.description}</div>
                                <div className="text-[10px] font-medium opacity-60 flex items-center gap-2">
                                  <span>{t.date}</span>
                                  <span>•</span>
                                  <span>{t.type}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-lg font-black ${isSelected ? 'text-emerald-400' : 'text-zinc-200'}`}>
                                {formatCurrency(t.details[0]?.amount || 0)}
                              </div>
                              <div className="text-[9px] uppercase tracking-widest font-bold opacity-40">Total Orig.</div>
                              <div className="text-[10px] font-bold text-zinc-500 mt-0.5">Disp: {formatCurrency(getAvailableTransactionAmount(t.id))}</div>
                            </div>
                          </button>
                        );
                      })
                    }
                  </div>

                  <div className="pt-6 border-t border-zinc-800 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-bold text-zinc-500 uppercase">Seleccionados: {selectedTransactionIds.length}</div>
                      <div className="text-sm font-black text-white">{formatCurrency(transactionsTotal)}</div>
                    </div>
                    <button 
                      onClick={() => setShowTransactionSelector(false)}
                      className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/20"
                    >
                      LISTO
                    </button>
                  </div>
                </div>
             </motion.div>
          </div>
        )}

        {showContactCreator && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
             <motion.div 
               initial={{ scale: 0.9, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-lg overflow-hidden"
             >
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                  <h3 className="font-bold text-white">Nuevo Contacto Rápido</h3>
                  <button onClick={() => setShowContactCreator(false)} className="p-2 hover:bg-zinc-800 rounded-lg"><X size={20}/></button>
                </div>
                <QuickContactForm 
                  initialName={(contactSearch || contactName).includes('-') ? '' : (contactSearch || contactName)}
                  initialTaxId={newContactTaxId || ((contactSearch || contactName).match(/[\d-]+/) ? (contactSearch || contactName) : '')}
                  userId={userId}
                  isPurchase={type.includes('PURCHASE') || type.includes('EXPENSE')}
                  onSuccess={(contact) => {
                    setContactId(contact.id);
                    setContactName(contact.name);
                    setShowContactCreator(false);
                    setNewContactTaxId('');
                  }}
                  onCancel={() => setShowContactCreator(false)}
                />
             </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-red-500/20 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl p-6 space-y-6"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center text-red-500">
                  <Trash2 size={32} />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">¿Eliminar documento?</h3>
                  <p className="text-sm text-zinc-400 mt-2">
                    Estás a punto de eliminar el documento <strong className="text-white">{editingDocument?.number}</strong>. Esta acción no se puede deshacer y desvinculará todos los pagos asociados.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                  className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={executeDelete}
                  disabled={loading}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : 'Sí, eliminar'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-emerald-500/20 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl p-6 space-y-6"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center text-emerald-500">
                  <CheckCircle2 size={32} />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">¡Registro Exitoso!</h3>
                  <p className="text-sm text-zinc-400 mt-3">
                    El documento ha sido guardado correctamente. Por favor, escribe el siguiente código de referencia en el papel físico para facilitar auditorías futuras:
                  </p>
                </div>

                <div className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl p-4 mt-2">
                  <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Código de Referencia</div>
                  <div className="text-3xl font-black text-emerald-400 font-mono tracking-[0.3em] uppercase">
                    {registeredLocatorCode}
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => {
                  setShowSuccessModal(false);
                  onSuccess();
                }}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2 group"
              >
                Continuar <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const QuickContactForm: React.FC<{ 
  initialName: string, 
  initialTaxId: string, 
  userId: string, 
  isPurchase: boolean,
  onSuccess: (c: ManagedContact) => void,
  onCancel: () => void 
}> = ({ initialName, initialTaxId, userId, isPurchase, onSuccess, onCancel }) => {
  const [name, setName] = useState(initialName);
  const [taxId, setTaxId] = useState(initialTaxId);
  const [taxIdType, setTaxIdType] = useState<ContactIdType>('RUC');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await createContact(userId, {
        name,
        taxId,
        taxIdType,
        isCustomer: !isPurchase,
        isProvider: isPurchase
      });
      onSuccess({ id: res.id, name, taxId, taxIdType, isCustomer: !isPurchase, isProvider: isPurchase } as ManagedContact);
    } catch (e) {
      alert("Error al crear contacto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-500 uppercase px-1">Nombre</label>
        <input required value={name} onChange={e => setName(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-zinc-500 uppercase px-1">Tipo ID</label>
          <select value={taxIdType} onChange={e => setTaxIdType(e.target.value as ContactIdType)} className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm">
            {['RUC', 'CÉDULA DE IDENTIDAD', 'PASAPORTE', 'CÉDULA EXTRANJERO', 'SIN NOMBRE', 'DIPLOMÁTICO', 'IDENTIFICACIÓN TRIBUTARIA', 'CLIENTE DEL EXTERIOR'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-zinc-500 uppercase px-1">Nro ID</label>
          <input required value={taxId} onChange={e => setTaxId(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm" />
        </div>
      </div>
      <div className="flex gap-2 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 py-3 bg-zinc-800 rounded-xl font-bold text-sm">Cancelar</button>
        <button type="submit" disabled={loading} className="flex-1 py-3 bg-emerald-600 rounded-xl font-bold text-sm flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Guardar
        </button>
      </div>
    </form>
  )
}
